import { markedByAnyOfMine } from './jrStaffPerPersonState.ts';
import type { InboxViewerUids } from './messageInboxAudience.ts';
import type { ClassPostDoc } from './classPosts';
import { tsToMillis } from './tonight.ts';

type PersonalInboxClassPost = Pick<
  ClassPostDoc,
  | 'offeringId'
  | 'authorUid'
  | 'expectedRecipientUids'
  | 'acks'
  | 'dismissedBy'
  | 'reactions'
> & {
  /** Explicit moderation fields used by current or migrated class-board docs. */
  moderationRequired?: boolean;
  flaggedForModeration?: boolean;
  moderationStatus?: string;
};

export interface PersonalClassPostInboxContext {
  /**
   * The viewer, as ONE uid or as every sign-in she holds. A Jr Staff can hold
   * her dancer sign-in and her email staff account, linked by
   * users/{uid}.studentId, and a post that named one of them was invisible
   * from the other (Bobbie, 2026-09-07: *"zero difference other than how they
   * are set up"*). A bare string is unchanged behaviour.
   */
  viewerUid: InboxViewerUids;
  taughtOfferingIds: ReadonlySet<string>;
  familyOfferingIds: ReadonlySet<string>;
  /** Only a Director/moderator should turn an explicit flag into inbox work. */
  canReviewModeration: boolean;
}

/**
 * Read access is deliberately broader than personal-inbox targeting for
 * staff. This predicate is the second boundary: ordinary class-board traffic
 * belongs in a person's Messages only when they have a concrete relationship
 * to the class or post. Studio-wide review remains in Command Center.
 */
export function classPostBelongsInPersonalInbox(
  post: PersonalInboxClassPost,
  context: PersonalClassPostInboxContext
): boolean {
  const mine = (typeof context.viewerUid === 'string' ? [context.viewerUid] : [...context.viewerUid])
    .map((uid) => (typeof uid === 'string' ? uid.trim() : ''))
    .filter((uid) => uid.length > 0);
  if (mine.length === 0) return false;
  if (context.taughtOfferingIds.has(post.offeringId)) return true;
  if (context.familyOfferingIds.has(post.offeringId)) return true;
  if (typeof post.authorUid === 'string' && mine.includes(post.authorUid)) return true;
  const expected = post.expectedRecipientUids ?? [];
  if (mine.some((uid) => expected.includes(uid))) return true;
  // READ SIDE ONLY. A mark left under EITHER of her sign-ins is her mark, so
  // a post she already acked on one account does not reappear as new work on
  // the other. The WRITE side stays on the uid actually signed in: a client
  // writing under another uid is refused by the rules, and pretending
  // otherwise here would draw a door that slams.
  if (markedByAnyOfMine(post.acks, mine)
    || markedByAnyOfMine(post.dismissedBy, mine)
    || markedByAnyOfMine(post.reactions, mine)) {
    return true;
  }
  return context.canReviewModeration && (
    post.moderationRequired === true
    || post.flaggedForModeration === true
    || post.moderationStatus === 'pending'
  );
}

export interface ClassPostInboxGroup {
  id: string;
  broadcastId: string | null;
  posts: ClassPostDoc[];
  representative: ClassPostDoc;
}

function contentFingerprint(post: ClassPostDoc): string {
  return JSON.stringify({
    body: post.body.trim(),
    postType: post.postType ?? 'update',
    ackRequired: Boolean(post.ackRequired),
    studentIds: [...(post.studentIds ?? [])].sort(),
    importantDates: (post.importantDates ?? []).map((date) => ({
      id: date.id,
      title: date.title,
      date: date.date,
      startTime: date.startTime ?? null,
      endTime: date.endTime ?? null,
      note: date.note ?? null,
    })),
    imageUrl: post.imageUrl ?? null,
    videoUrl: post.videoUrl ?? null,
  });
}

/**
 * A one-tap studio send fans out to many class boards, but it should read as
 * one message in the merged inbox. Ordinary class posts stay independent.
 */
export function groupClassPostsForInbox(posts: readonly ClassPostDoc[]): ClassPostInboxGroup[] {
  const groups = new Map<string, ClassPostInboxGroup>();
  const broadcastVariantCounts = new Map<string, number>();

  for (const post of posts) {
    const broadcastId = post.broadcastId?.trim() || null;
    const mapKey = broadcastId
      ? `${broadcastId}\u0000${contentFingerprint(post)}`
      : `post\u0000${post.id}`;
    const existing = groups.get(mapKey);
    if (existing) {
      existing.posts.push(post);
      continue;
    }
    const variantNumber = broadcastId ? (broadcastVariantCounts.get(broadcastId) ?? 0) + 1 : 1;
    if (broadcastId) broadcastVariantCounts.set(broadcastId, variantNumber);
    const id = broadcastId
      ? `classPostBroadcast-${broadcastId}${variantNumber === 1 ? '' : `-variant-${variantNumber}`}`
      : `classPost-${post.id}`;
    groups.set(mapKey, {
      id,
      broadcastId,
      posts: [post],
      representative: post,
    });
  }

  return [...groups.values()];
}

/**
 * How many EXTRA raw documents the studio-wide staff read
 * (classPosts.ts's useClassPostInbox) scans past its own display cap before
 * giving up.
 *
 * THE EVICTION BUG this exists to close (proven with a failing-then-passing
 * test below): the old query read the newest CLASS_POST_INBOX_CAP documents
 * STUDIO WIDE and only THEN grouped/filtered locally. One one-tap send
 * writes one classPost per class board - Bobbie's studio runs close to
 * seventy, and a studio-wide cancellation can touch a similar count - so
 * those alone filled the entire window and every teacher's own, unrelated
 * class post fell off the bottom before groupClassPostsForInbox above (the
 * SAME collapse the merged inbox already displays with) ever ran on them.
 *
 * The fix moves that same collapse to run BEFORE the cap instead of after -
 * see collapseClassPostInboxWindow below - which needs the raw read to see
 * far enough PAST one full broadcast to reach the ordinary posts sitting
 * behind it. Bounded, not unbounded: this comfortably absorbs one large send
 * (or several) without chasing every possible pile-up, and a raw read that
 * still exhausts this headroom is reported honestly through `capped`, same
 * as before.
 *
 * Lives here, not in classPosts.ts, so it can be unit-tested with plain
 * fixture posts - classPosts.ts pulls in the Firebase SDK at module load and
 * cannot be imported from a plain node:test file (see that file's own
 * header), the same constraint messageInbox.ts's own header already notes
 * for groupClassPostsForInbox.
 */
const CLASS_POST_INBOX_RAW_HEADROOM = 250;
/** Hard ceiling on the raw read regardless of headroom, so a runaway studio
 *  size can never turn this into an unbounded live listener. */
const CLASS_POST_INBOX_RAW_FETCH_MAX = 500;

/** How many raw documents the staff-wide inbox read asks Firestore for. */
export function classPostInboxRawFetchLimit(maxPosts: number): number {
  return Math.min(maxPosts + CLASS_POST_INBOX_RAW_HEADROOM, CLASS_POST_INBOX_RAW_FETCH_MAX);
}

/**
 * The staff-wide inbox window: collapse a raw, newest-first read into the
 * display list a director or teacher actually sees, folding every post that
 * shares one broadcast send (groupClassPostsForInbox above) into ONE slot
 * before `maxPosts` is applied, not after. A seventy-post send now costs one
 * slot in the window instead of seventy, so an unrelated, older post from a
 * teacher's own class board stays inside the cap instead of being pushed out
 * by copies of the same broadcast.
 */
export function collapseClassPostInboxWindow(
  livePosts: readonly ClassPostDoc[],
  maxPosts: number
): { posts: ClassPostDoc[]; capped: boolean } {
  const groups = groupClassPostsForInbox(livePosts);
  const posts = groups
    .slice(0, maxPosts)
    .flatMap((group) => group.posts)
    // groups are already newest-first (Map insertion order follows the
    // newest-first input), but a group's OWN members can interleave with a
    // later group's - e.g. group A's second post is genuinely older than
    // group B's only post - so the flattened list is re-sorted rather than
    // trusted to already be one strict recency order.
    .sort((a, b) => (tsToMillis(b.createdAt) ?? 0) - (tsToMillis(a.createdAt) ?? 0));
  return { posts, capped: groups.length > maxPosts };
}
